import { moduleParams } from '@/routes'
import type {
	CustomLocationHook,
	CustomSetLocation,
} from '@/types/useCustomLocation'
import { useCallback } from 'react'
import { type BaseLocationHook, type BaseSearchHook } from 'wouter'
import { useModules } from './useModule'

export function makeUseCustomLocation(
	useBaseLocation: BaseLocationHook,
	useBaseSearch: BaseSearchHook,
): CustomLocationHook {
	let prevLocation: string | null = null
	let prevSearch: string | null = null

	return function useLocation() {
		const [location, rawSetLocation] = useBaseLocation()
		const modules = useModules()
		const search = useBaseSearch()

		prevLocation = location
		prevSearch = search

		const setLocation = useCallback<CustomSetLocation>(
			(
				path,
				{
					replace = false,
					removeParams = [],
					setParams = [],
					clearParams = false,
				} = {},
			) => {
				const currentSearch = new URLSearchParams(prevSearch!)
				const nextUrl = new URL(
					path,
					new URL(prevLocation!, 'http://fakepath'),
				)
				if (!path) nextUrl.search = '?' + currentSearch.toString()

				const nextModule = modules.find(
					module =>
						module.path === nextUrl.pathname ||
						nextUrl.pathname.startsWith(module.path + '/'),
				)
				const retainedParams = new Set(
					nextModule ? moduleParams(nextModule) : [],
				)

				for (const [k, v] of currentSearch.entries()) {
					if (retainedParams.has(k) && !nextUrl.searchParams.has(k)) {
						nextUrl.searchParams.set(k, v)
					}
				}

				if (clearParams) nextUrl.search = ''

				for (const param of removeParams) {
					nextUrl.searchParams.delete(param)
				}

				if (setParams instanceof Map) {
					for (const [key, value] of setParams.entries()) {
						nextUrl.searchParams.set(key, value)
					}
				} else if (setParams) {
					for (const [key, value] of setParams) {
						nextUrl.searchParams.set(key, value)
					}
				}

				if (nextUrl.origin !== 'http://fakepath') {
					window.location.href = nextUrl.href
					return
				}

				prevLocation = nextUrl.pathname
				prevSearch = nextUrl.search.slice(1)

				rawSetLocation(nextUrl.pathname + nextUrl.search, { replace })
			},
			[rawSetLocation, modules],
		)

		return [location, setLocation]
	}
}
